import { Scene, Vector3, MeshBuilder, Mesh, PointLight, Color3 } from "@babylonjs/core";
import { BaseZone } from "./BaseZone.js";
import {
  WingName,
  FLOORS,
  FLOOR_HEIGHT,
  ATRIUM_HALF,
  CORRIDOR_HALF_WIDTH,
  CORRIDOR_LENGTH,
  WING_MARGIN,
} from "@mall-mart/shared";
import { getSharedMaterials } from "../materials.js";
import { DynamicTextureSurface } from "../surfaces/DynamicTextureSurface.js";
import { mergeStaticChildren } from "../mergeStatic.js";
import { buildBench, buildPlanterTree } from "../props.js";

/**
 * A wing: two stacked 10 m corridors (ground + upper floor at y=7) lined
 * with shop units on both sides. Builds floor slabs, ceilings, margin wall
 * filler, fascia bands, a full-height end wall with per-floor signage, and
 * benches/planters/lights per floor. The upper corridor is reached from the
 * atrium gallery (escalators live in the atrium).
 */
export class WingZone extends BaseZone {
  private readonly wing: WingName;
  private readonly title: string;
  private floors: Mesh[] = [];

  constructor(scene: Scene, wing: WingName, title: string) {
    super(`wing-${wing}`, scene, new Vector3(0, 0, 0));
    this.wing = wing;
    this.title = title;
  }

  /** Transform corridor-local (lateral, along) to world space per wing. */
  private toWorld(lateral: number, along: number, y: number): Vector3 {
    if (this.wing === "north") {
      return new Vector3(lateral, y, along);
    }
    if (this.wing === "east") {
      return new Vector3(along, y, -lateral);
    }
    return new Vector3(-along, y, lateral); // west
  }

  private yawToWorld(localYaw: number): number {
    if (this.wing === "north") return localYaw;
    if (this.wing === "east") return localYaw + Math.PI / 2;
    return localYaw - Math.PI / 2;
  }

  /** Axis-aware box dims: a = along-corridor extent, l = lateral extent. */
  private dims(a: number, l: number): { width: number; depth: number } {
    return this.wing === "north" ? { width: l, depth: a } : { width: a, depth: l };
  }

  async build(): Promise<void> {
    const mats = getSharedMaterials(this.scene);
    const start: number = ATRIUM_HALF;
    const end: number = ATRIUM_HALF + CORRIDOR_LENGTH;
    const mid: number = (start + end) / 2;
    const w: number = CORRIDOR_HALF_WIDTH * 2;

    for (let f: number = 0; f < FLOORS; f++) {
      const base: number = f * FLOOR_HEIGHT;

      // Floor: ground gets a flat ground mesh; the upper level a slab whose
      // underside doubles as the ground corridor's ceiling.
      if (f === 0) {
        const floor: Mesh = MeshBuilder.CreateGround(
          `${this.id}-floor-0`, this.wing === "north" ? { width: w, height: CORRIDOR_LENGTH } : { width: CORRIDOR_LENGTH, height: w }, this.scene
        );
        const c: Vector3 = this.toWorld(0, mid, 0);
        floor.position = new Vector3(c.x, 0, c.z);
        floor.material = mats.corridorFloor;
        floor.parent = this.root;
        floor.checkCollisions = true;
        this.floors.push(floor);
      } else {
        const slab: Mesh = MeshBuilder.CreateBox(
          `${this.id}-floor-${f}`, { ...this.dims(CORRIDOR_LENGTH, w + 2), height: 0.4 }, this.scene
        );
        slab.position = this.toWorld(0, mid, base - 0.2);
        slab.material = mats.corridorFloor;
        slab.parent = this.root;
        slab.checkCollisions = true;
        this.floors.push(slab);

        // White ceiling skin under the slab: its bare underside is polished
        // tile, which reads as a heavy dark ceiling over the ground corridor.
        const underCeil: Mesh = MeshBuilder.CreateBox(
          `${this.id}-underceil-${f}`, { ...this.dims(CORRIDOR_LENGTH, w + 2), height: 0.05 }, this.scene
        );
        underCeil.position = this.toWorld(0, mid, base - 0.43);
        underCeil.material = mats.ceiling;
        underCeil.parent = this.root;
      }

      // Margin wall filler at both corridor ends, both sides, this floor.
      for (const side of [-1, 1]) {
        for (const seg of [
          { from: start, len: WING_MARGIN },
          { from: end - WING_MARGIN, len: WING_MARGIN },
        ]) {
          const wall: Mesh = MeshBuilder.CreateBox(
            `${this.id}-fill-${f}-${side}-${seg.from}`,
            { ...this.dims(seg.len, 0.3), height: 6 },
            this.scene
          );
          wall.position = this.toWorld(side * CORRIDOR_HALF_WIDTH, seg.from + seg.len / 2, base + 3);
          wall.material = mats.corridorWall;
          wall.parent = this.root;
          wall.checkCollisions = true;
        }
        // Fascia band above the storefronts (top metre of the floor).
        const fascia: Mesh = MeshBuilder.CreateBox(
          `${this.id}-fascia-${f}-${side}`,
          { ...this.dims(CORRIDOR_LENGTH, 0.3), height: 1 },
          this.scene
        );
        fascia.position = this.toWorld(side * CORRIDOR_HALF_WIDTH, mid, base + 6.5);
        fascia.material = mats.corridorWall;
        fascia.parent = this.root;

        // Neon accent line under the fascia, running the corridor's length.
        const neon: Mesh = MeshBuilder.CreateBox(
          `${this.id}-neon-${f}-${side}`,
          { ...this.dims(CORRIDOR_LENGTH, 0.12), height: 0.08 },
          this.scene
        );
        neon.position = this.toWorld(side * (CORRIDOR_HALF_WIDTH - 0.1), mid, base + 5.96);
        neon.material = mats.neonAccent;
        neon.parent = this.root;
      }

      // Wing title sign on the end wall, per floor (same dark wayfinding
      // language as the atrium signage).
      const sign: DynamicTextureSurface = new DynamicTextureSurface({
        scene: this.scene,
        name: `${this.id}-sign-${f}`,
        widthM: 8,
        heightM: 1.6,
        resolution: { width: 1024, height: 205 },
        theme: {
          background: "#161b21",
          foreground: "#eef3f7",
          accent: "#6fb3e0",
          fontFamily: "sans-serif",
          fontFamilySerif: "serif",
        },
        emissive: true,
        layout: "wayfinding",
      });
      sign.placeAt({
        position: this.toWorld(0, end - 0.4, base + 5),
        rotationY: this.yawToWorld(Math.PI),
        parent: this.root,
      });
      sign.setContent({
        title: this.title,
        subtitle: f === 0 ? "Ground Floor" : "Upper Floor",
      });

      // Benches + planters down the middle, ceiling lights, per floor.
      for (let i: number = 0; i < 3; i++) {
        const along: number = start + 10 + i * 18;
        // Back-to-back pair down the corridor centerline (backrests meet in
        // the middle) so seating faces BOTH sides of the hallway, not one.
        buildBench(
          this.scene, this.root, `${this.id}-bench-${f}-${i}-a`,
          this.toWorld(-0.4, along, base), this.yawToWorld(Math.PI / 2)
        );
        buildBench(
          this.scene, this.root, `${this.id}-bench-${f}-${i}-b`,
          this.toWorld(0.4, along, base), this.yawToWorld(-Math.PI / 2)
        );
        buildPlanterTree(
          this.scene, this.root, `${this.id}-tree-${f}-${i}`,
          this.toWorld(0, along + 8, base)
        );

        const light: PointLight = new PointLight(
          `${this.id}-light-${f}-${i}`,
          this.toWorld(0, along, base + FLOOR_HEIGHT - 1),
          this.scene
        );
        light.diffuse = new Color3(0.96, 0.97, 1.0);
        light.intensity = 0.55;
        light.range = 24;
        light.parent = this.root;
      }
    }

    // Top ceiling above the upper corridor.
    const ceil: Mesh = MeshBuilder.CreateBox(
      `${this.id}-ceil`, { ...this.dims(CORRIDOR_LENGTH, w + 2), height: 0.3 }, this.scene
    );
    ceil.position = this.toWorld(0, mid, FLOORS * FLOOR_HEIGHT);
    ceil.material = mats.ceiling;
    ceil.parent = this.root;

    // End wall spans both floors.
    const endWall: Mesh = MeshBuilder.CreateBox(
      `${this.id}-end`, { ...this.dims(0.4, w + 0.6), height: FLOORS * FLOOR_HEIGHT }, this.scene
    );
    endWall.position = this.toWorld(0, end, (FLOORS * FLOOR_HEIGHT) / 2);
    endWall.material = mats.corridorWall;
    endWall.parent = this.root;
    endWall.checkCollisions = true;

    // Guard rails at the upper corridor mouth are handled by the atrium
    // gallery railings; the corridor floor meets the gallery slab flush.

    mergeStaticChildren(this.root, (m) => this.floors.includes(m));
  }

  override floorMeshes(): Mesh[] {
    return this.floors;
  }
}
